// API utility functions for moderator management
import { message } from 'antd';

// Base API configuration - adjust the base URL according to your setup
const API_BASE_URL = import.meta.env.VITE_APP_API;;

class ModeratorAPI {
  constructor() {
    const authData = JSON.parse(localStorage.getItem("faceAuthTokenData"));
    this.token = authData?.token?.access;
  }

  // Helper method to make authenticated requests
  async makeRequest(url, options = {}) {
    const defaultHeaders = {
      'Content-Type': 'application/json',
      ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
    };

    const config = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(`${API_BASE_URL}${url}`, config);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Get all moderators
  async getModerators() {
    return this.makeRequest('/api/account/moderators');
  }

  // Get single moderator details
  async getModerator(moderatorId) {
    return this.makeRequest(`/api/account/moderators/${moderatorId}/`);
  }

  // Get available modules
  async getAvailableModules() {
    return this.makeRequest('/api/account/available_modules');
  }

  // Get all users for selection
  async getUsers(searchTerm = '') {
    const url = searchTerm 
      ? `/api/account/account_list?search=${encodeURIComponent(searchTerm)}`
      : '/api/account/account_list';
    return this.makeRequest(url);
  }

  // Add new moderator
  async addModerator(moderatorData) {
    return this.makeRequest('/api/account/add_moderator', {
      method: 'POST',
      body: JSON.stringify(moderatorData),
    });
  }

  // Update moderator
  async updateModerator(moderatorId, moderatorData) {
    return this.makeRequest(`/api/account/moderators/${moderatorId}/`, {
      method: 'PUT',
      body: JSON.stringify(moderatorData),
    });
  }

  // Remove moderator
  async removeModerator(moderatorId) {
    return this.makeRequest(`/api/account/remove_moderator/${moderatorId}`, {
      method: 'DELETE',
    });
  }

  // Toggle moderator active status
  async toggleModeratorStatus(moderatorId) {
    return this.makeRequest(`/api/account/moderators/${moderatorId}/toggle_active/`, {
      method: 'POST',
    });
  }

  // Check module access for current user
  async checkModuleAccess(moduleName) {
    return this.makeRequest('/api/account/check_module_access', {
      method: 'POST',
      body: JSON.stringify({ module: moduleName }),
    });
  }
}

// Export singleton instance
export const moderatorAPI = new ModeratorAPI();

// Helper functions for common operations
export const handleAPIError = (error, defaultMessage = 'Operation failed') => {
  if (error.response?.data?.msg) {
    message.error(error.response.data.msg);
  } else if (error.message) {
    message.error(error.message);
  } else {
    message.error(defaultMessage);
  }
};

export const validateModeratorData = (data) => {
  const errors = [];
  
  if (!data.account) {
    errors.push('User selection is required');
  }
  
  // Check if at least one module is selected
  const moduleKeys = [
    'can_access_courses', 'can_access_books', 'can_access_form',
    'can_access_sms_history', 'can_access_pre_booking', 'can_access_mentor_activity',
    'can_access_classes', 'can_access_exams', 'can_edit_profile',
    'can_access_admission_news', 'can_add_routine', 'can_access_notifications', 'can_add_result'
  ];
  
  const hasAnyModule = moduleKeys.some(key => data[key]);
  if (!hasAnyModule) {
    errors.push('At least one module access must be granted');
  }
  
  return errors;
};